<template>
  <v-card dark color="primary">
    <v-card-title>
      <v-text-field v-model="search" dark append-icon="mdi-magnify" />
    </v-card-title>
    <v-card-text>
      <v-data-iterator
        :items="characterTraits"
        item-key="name"
        :items-per-page="characterTraits.length"
        :search="search"
        hide-default-footer
        single-expand
        dark>
        <template v-slot:default="{ items, isExpanded, expand }">
          <v-row>
            <v-col
              v-for="trait in items"
              :key="trait.name"
              cols="12"
              sm="6"
              md="4"
              xl="3">
              <v-card color="secondary">
                <v-card-title>
                  <avatarStat :traitType="trait.type.toLowerCase()" />
                  &nbsp;
                  <h4>{{ trait.name }}</h4>
                </v-card-title>
                <v-card-text v-if="isExpanded(trait)">
                  <v-sheet color="primary" class="text--white">
                    <contentEngine
                      :content="trait.description"
                      contentTitle=""
                      headerType="None"
                      leftAlign
                      :editMode="false"
                      :contentTagging="contentTagging"
                      :sections="sections"
                      :examples="{}" />
                    <br>
                  </v-sheet>
                </v-card-text>
                <v-card-actions>
                  <v-switch
                    :input-value="isExpanded(trait)"
                    :label="isExpanded(trait) ? 'Hide Details' : 'View Details'"
                    @change="(v) => expand(trait, v)" />
                  <v-spacer />
                  <v-checkbox
                    v-model="character.resetTraits"
                    @change="traitAction(trait.name)"
                    label="Select Trait"
                    :value="trait.name" />
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </v-data-iterator>
    </v-card-text>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';

export default {
  components: {
    avatarStat: () => import('../avatarStat'),
    contentEngine: () => import('../../contentEngine/contentEngine')
  },
  props: {
    character: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      search: ''
    };
  },
  watch: {
    characterTraits() {
      // When a trait is un-selected we have to ensure that all the requirements for other traits that may have been chosen after that one are still able and get rid of it if not.
      const faultyValues = [];
      this.character.resetTraits.forEach((selection) => {
        if (!this.characterTraits.find(trait => trait.name === selection)) {
          faultyValues.push(selection);
        }
      });
      faultyValues.forEach((val) => {
        const index = this.character.resetTraits.indexOf(val);
        this.character.resetTraits.splice(index, 1);
        this.traitAction(val);
      });
    }
  },
  methods: {
    checkRequirements(trait) {
      let returnVal = true;
      if (this.character.traits.includes(trait.name)) {
        returnVal = false;
      } else {
        trait.requirements.forEach((req) => {
          if (req.includes(':')) {
            const reqStat = req.split(':')[0];
            const reqValue = parseInt(req.split(':')[1], 10);
            if (!(this.character[reqStat.toLowerCase()] >= reqValue)) {
              returnVal = false;
            }
          } else {
            if (!this.character.traits.includes(req) && !this.character.resetTraits.includes(req)) {
              returnVal = false;
            }
          }
        });
      }
      return returnVal;
    },
    traitAction(trait) {
      if (trait.includes('Spiritual Boost')) {
        if (this.character.resetTraits.includes(trait)) {
          this.character.spirit--;
        } else {
          this.character.spirit++;
        }
      }

      if (trait.includes('Faultless')) {
        if (this.character.resetTraits.includes(trait)) {
          this.character[this.character.dumpStats[0].toLowerCase()]++;
        } else {
        this.character[this.character.dumpStats[0].toLowerCase()]--;
        }
      }
      
      if (['Mindful Tank', 'Spiritual Tank', 'Nimble Understudy', 'Spiritual Understudy', 'Mindful Warrior', 'Nimble Warrior', 'Powerful Scoundrel', 'Spiritual Scoundrel', 'Mindful Adventurer', 'Powerful Adventurer'].includes(trait)) {
        const newPrimary = this.character.tertiaryStats[0];
        this.character.primaryStats.push(newPrimary);
        this.character.tertiaryStats = [];
      }
    }
  },
  computed: {
    ...mapState(['contentTagging']),
    ...mapState('actsTraits', ['traits']),
    ...mapState('actsContent', ['sections']),
    characterTraits() {
      // Determine which trait types the character is able to take based off of his stats and traits
      let acolyte = false;
      if (this.character.resetTraits.length === this.character.traitPoints) {
        return this.traits.filter(trait => this.character.resetTraits.includes(trait.name));
      }
      let traitTypes = ['general'];
      if (this.character.primaryStats.includes('Physique') && this.character.primaryStats.includes('Finesse')) {
        traitTypes.push('physique-finesse');
      }
      if (this.character.primaryStats.includes('Physique') && this.character.primaryStats.includes('Mind')) {
        traitTypes.push('physique-mind');
      }
      if (this.character.primaryStats.includes('Physique') && this.character.primaryStats.includes('Spirit')) {
        traitTypes.push('physique-spirit');
      }
      if (this.character.primaryStats.includes('Finesse') && this.character.primaryStats.includes('Mind')) {
        traitTypes.push('finesse-mind');
      }
      if (this.character.primaryStats.includes('Finesse') && this.character.primaryStats.includes('Spirit')) {
        traitTypes.push('finesse-spirit');
      }
      if (this.character.primaryStats.includes('Mind') && this.character.primaryStats.includes('Spirit')) {
        traitTypes.push('mind-spirit');
      } else if (this.character.traits.includes('Acolyte') || this.character.resetTraits.includes('Acolyte')) {
        acolyte = true;
      }
      this.character.primaryStats.forEach((stat) => {
        traitTypes.push(stat.toLowerCase());
      });
      this.character.tertiaryStats.forEach((stat) => {
        traitTypes.push(stat.toLowerCase());
      });
      if (this.character.resetTraits.includes('Outside Knowledge')) {
        this.character.dumpStats.forEach((stat) => {
          traitTypes.push(stat.toLowerCase());
        });
      }
      let matchingTraits = this.traits.filter(trait => traitTypes.includes(trait.type));
      if (acolyte) {
        matchingTraits = matchingTraits.concat(this.traits.filter(trait => trait.name.includes('(Spell)')));
      }
      const returnTraits = matchingTraits.filter(trait => this.checkRequirements(trait));
      return returnTraits;
    }
  }
};
</script>

<style>

</style>
